import { BadgeCheck, RefreshCw } from 'lucide-react';
import { useSyncCustomer } from '@/api/customers';
import type { Customer } from '@/api/types';
import { Button, useToast } from '@/components/ui';
import { useAuth } from '@/app/auth/useAuth';
import { cn } from '@/lib/cn';
import { formatRelative } from '@/lib/format';

/** "instagramFollowers" → "Instagram followers" */
function label(key: string): string {
  const s = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function display(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') return new Intl.NumberFormat('en-IN').format(v);
  return String(v);
}

export function VerifiedBadge({ at, compact = false }: { at: string | null | undefined; compact?: boolean }) {
  if (!at) return null;
  return (
    <span
      title={`Signed-in user · verified ${formatRelative(at)}`}
      className={cn(
        'inline-flex items-center gap-1 rounded-full bg-emerald-50 font-semibold text-emerald-700 ring-1 ring-emerald-200 ring-inset',
        compact ? 'p-0.5' : 'px-2 py-0.5 text-[11px]',
      )}
    >
      <BadgeCheck className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} aria-hidden />
      {compact ? <span className="sr-only">Verified user</span> : 'Verified user'}
    </span>
  );
}

/** Profile fields synced from the customer's platform account, plus a manual refresh. */
export function KollabrAttributes({ customer }: { customer: Customer }) {
  const { can } = useAuth();
  const toast = useToast();
  const sync = useSyncCustomer();
  const entries = Object.entries(customer.attributes ?? {});
  if (!customer.kollabrUserId) return null;

  return (
    <div className="rounded-xl bg-gradient-to-br from-emerald-50/70 to-sky-50/50 p-3 ring-1 ring-emerald-100">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-slate-700">
          Platform profile{' '}
          <span className="font-mono font-normal text-slate-400">#{customer.kollabrUserId}</span>
        </p>
        {can('update', 'Customer') && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            loading={sync.isPending}
            onClick={() =>
              sync.mutate(customer.id, {
                onSuccess: () => toast.success('Profile refreshed'),
                onError: (e) => toast.error(e.message),
              })
            }
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </Button>
        )}
      </div>
      {entries.length === 0 ? (
        <p className="text-xs text-slate-500">
          No extra fields yet. Configure the user lookup in Settings → Chat widget → Users.
        </p>
      ) : (
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
          {entries.map(([k, v]) => (
            <div key={k} className="min-w-0">
              <dt className="truncate text-[11px] text-slate-500">{label(k)}</dt>
              <dd className="truncate text-sm font-medium text-slate-800" title={display(v)}>
                {display(v)}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
